import { useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  IconButton,
  keyframes,
  reactIcons,
  TextArea,
  toast,
  Typography,
} from '@project44/manifest-ds';

import { scopedT } from '../../../utils/functions/Translations';
import { sendEmail } from './sendEmail';

const t = scopedT();

const rotate = keyframes({
  from: {
    transform: 'rotate(0deg)',
  },
  to: {
    transform: 'rotate(359deg)',
  },
});

type RequestType = 'access' | 'questions' | 'feedback';

interface StateType {
  requests: Record<RequestType, boolean>;
  message: string;
}

const checkboxes: Record<RequestType, string> = {
  access: t('getInTouch.checkboxes.access'),
  questions: t('getInTouch.checkboxes.questions'),
  feedback: t('getInTouch.checkboxes.feedback'),
};

interface Props {
  onClose: () => void;
}

function GetInTouchModal({ onClose }: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const [state, setState] = useState<StateType>({
    message: '',
    requests: {
      access: false,
      questions: false,
      feedback: false,
    },
  });

  const onSubmitClicked = async () => {
    setIsLoading(true);
    try {
      await sendEmail({
        message: state.message,
        access: state.requests.access,
        questions: state.requests.questions,
        feedback: state.requests.feedback,
      });
      onClose();
      toast(t('getInTouch.messageSent.label'), {
        description: t('getInTouch.messageSent.description'),
        variant: 'success',
      });
    } catch (e: unknown) {
      setIsLoading(false);
    }
  };

  const renderCheckbox = (alias: RequestType) => {
    return (
      <Checkbox
        aria-label={checkboxes[alias]}
        css={{ marginTop: '$small' }}
        isSelected={state.requests[alias]}
        onChange={(val) => {
          setState({
            ...state,
            requests: {
              ...state.requests,
              [alias]: val,
            },
          });
        }}
      >
        {checkboxes[alias]}
      </Checkbox>
    );
  };

  return (
    <Dialog data-testid="get-in-touch-modal" onClose={onClose} isOpen>
      <DialogHeader>{t('getInTouch.heading')}</DialogHeader>
      <DialogContent css={{ width: 400 }}>
        <Flex orientation="vertical">
          <Typography variant="body">{t('getInTouch.subHeading')}</Typography>
          <Typography variant="subtextBold" css={{ marginTop: '$medium' }}>
            {t('getInTouch.whyGettingInTouch')}
          </Typography>
        </Flex>
        <Flex orientation="vertical" css={{ marginTop: '$small' }}>
          {renderCheckbox('access')}
          {renderCheckbox('questions')}
          {renderCheckbox('feedback')}
        </Flex>
        <Box css={{ marginTop: '$medium' }}>
          <Typography variant="subtextBold">
            {t('getInTouch.tellUsMore')}:
          </Typography>
          <TextArea
            aria-label={t('getInTouch.tellUsMore')}
            css={{ textarea: { height: '120px !important' } }}
            placeholder={t('getInTouch.tellUsMorePlaceholder')}
            value={state.message}
            onChange={(val) => setState({ ...state, message: val })}
          ></TextArea>
        </Box>
      </DialogContent>
      <DialogFooter>
        <Button
          aria-label={t('global.cancel')}
          data-testid="get-in-touch-modal-cancel"
          variant="secondary"
          isDisabled={isLoading}
          onPress={onClose}
        >
          {t('global.cancel')}
        </Button>
        {isLoading ? (
          <IconButton
            data-testid="get-in-touch-modal-loading"
            isDisabled
            css={{
              padding: '0 $medium !important',
            }}
          >
            <Flex css={{ animation: `${rotate} 1s linear infinite` }}>
              <reactIcons.Reload />
            </Flex>
          </IconButton>
        ) : (
          <Button
            data-testid="get-in-touch-modal-submit"
            aria-label={t('global.submit')}
            onPress={onSubmitClicked}
          >
            {t('global.submit')}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

export default GetInTouchModal;
